import { FC, useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomAlert, CustomButton, CustomInput, CustomSelect, Text } from '../../shared/uiComponents';
import SearchSelect, { SelectDataProperties } from '../../shared/uiComponents/Dropdown/searchSelect';
import { errorNormalizer, formatClientName } from '../../shared/Helpers/functions';

import { DispatchProperties } from '../../redux/store';
import {
	createAdpRateMapping,
	getAdpRateMappings,
	updateAdpRateMapping,
} from '../../redux/State/clientSlice/adpRateMappingSlice';
import { AdpRateMappingProperties } from '../../redux/API/ClientAPIHelpers/adpRateMapping';
import { getUsers } from '../../redux/State/userSlice';
import { getClients } from '../../redux/State/clientSlice/clientSlice';
import { AdminHourServiceCode, AdminHours } from '../../redux/API/ClientAPIHelpers/adminHours';

interface FormProperties {
	editItem: AdpRateMappingProperties | null;
	onClose: () => void;
	open: boolean;
	serviceCode?: boolean;
}

const FormContent: FC<{ editItem: AdpRateMappingProperties | null; onClose: () => void; serviceCode: boolean }> = ({
	onClose,
	editItem,
	serviceCode,
}) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [userId, setUserId] = useState(editItem?.userId || '');
	const [clientId, setClientId] = useState(editItem?.clientId || '');
	const [rateId, setRateId] = useState(editItem?.rateId || '');
	const [codeId, setCodeId] = useState(editItem?.serviceCodeId || '');
	const [codes, setCodes] = useState<AdminHourServiceCode[]>([]);
	useEffect(() => {
		if (serviceCode) AdminHours.getServiceCodes().then(setCodes).catch(errorNormalizer);
	}, [serviceCode]);
	const [loading, setLoading] = useState(false);

	const [userSearch, setUserSearch] = useState('');
	const [clientSearch, setClientSearch] = useState('');
	const [usersData, setUsersData] = useState<SelectDataProperties[]>([]);
	const [clientsData, setClientsData] = useState<SelectDataProperties[]>([]);
	const [hasMoreUsers, setHasMoreUsers] = useState(false);
	const [hasMoreClients, setHasMoreClients] = useState(false);
	const [pageSizeUser, setPageSizeUser] = useState(10);
	const [pageSizeClient, setPageSizeClient] = useState(10);

	const isEdit = Boolean(editItem);

	useEffect(() => {
		if (isEdit) return;

		const timeout = setTimeout(() => {
			dispatch(getUsers({ page: '1', pageSize: `${pageSizeUser}`, searchString: userSearch }))
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					setUsersData(query.map((u) => ({ id: u.id, label: u.fullName })));
					setHasMoreUsers(Boolean(hasNextPage));
				});
		}, 500);

		// eslint-disable-next-line consistent-return
		return () => clearTimeout(timeout);
	}, [dispatch, userSearch, pageSizeUser, isEdit]);

	useEffect(() => {
		if (isEdit || serviceCode) return;

		const timeout = setTimeout(() => {
			dispatch(getClients({ page: '1', pageSize: `${pageSizeClient}`, searchString: clientSearch }))
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					setClientsData(query.map((c) => ({ id: c.id, label: formatClientName(c.fullName ?? '', c.isElc) })));
					setHasMoreClients(Boolean(hasNextPage));
				});
		}, 500);

		// eslint-disable-next-line consistent-return
		return () => clearTimeout(timeout);
	}, [dispatch, clientSearch, pageSizeClient, isEdit, serviceCode]);

	const handleSubmit = () => {
		if (!userId || !(serviceCode ? codeId : clientId) || !rateId.trim()) {
			toast('All fields are required.');

			return;
		}

		setLoading(true);
		const data = { userId, ...(serviceCode ? { serviceCodeId: codeId } : { clientId }), rateId: rateId.trim() };

		const action = editItem
			? dispatch(updateAdpRateMapping({ id: editItem.id, data }))
			: dispatch(createAdpRateMapping(data));

		action
			.then(unwrapResult)
			.then(() => {
				toast(isEdit ? 'Mapping updated' : 'Mapping created');
				dispatch(getAdpRateMappings());
				onClose();
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	return (
		<>
			{isEdit ? (
				<>
					<Text title={`Provider: ${editItem?.userFullName || editItem?.userId}`} className="marginBottom16" />
					<Text
						title={
							serviceCode
								? `Service code: ${editItem?.serviceCodeName || codeId}`
								: `Client: ${editItem?.clientFullName || clientId}`
						}
						className="marginBottom16"
					/>
				</>
			) : (
				<>
					<SearchSelect
						label="Provider"
						searchString={userSearch}
						setSearchString={setUserSearch}
						data={usersData}
						setValue={setUserId}
						hasMore={hasMoreUsers}
						setSize={setPageSizeUser}
						clearData={() => setUserId('')}
						className="marginBottom16"
					/>
					{serviceCode ? (
						<CustomSelect
							label="Service code"
							value={codeId}
							setValue={(event) => setCodeId(event.target.value)}
							data={codes.map((code) => ({ id: code.id, label: `${code.code} - ${code.name}` }))}
							className="marginBottom16"
						/>
					) : (
						<SearchSelect
							label="Client"
							searchString={clientSearch}
							setSearchString={setClientSearch}
							data={clientsData}
							setValue={setClientId}
							hasMore={hasMoreClients}
							setSize={setPageSizeClient}
							clearData={() => setClientId('')}
							className="marginBottom16"
						/>
					)}
				</>
			)}
			<CustomInput
				label="ADP Rate ID"
				value={rateId}
				onChange={(e) => setRateId(e.target.value)}
				className="marginBottom16"
			/>
			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch">
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} disabled={loading} />
				<CustomButton
					onClick={handleSubmit}
					title={isEdit ? 'Update Mapping' : 'Create Mapping'}
					loading={loading}
					disabled={!userId || !(serviceCode ? codeId : clientId) || !rateId.trim()}
				/>
			</Stack>
		</>
	);
};

const AdpRateMappingForm: FC<FormProperties> = ({ open, onClose, editItem, serviceCode = false }) => (
	<CustomAlert
		open={open}
		onClose={onClose}
		title={editItem ? 'Edit ADP Rate Mapping' : 'Add ADP Rate Mapping'}
		Content={() => <FormContent onClose={onClose} editItem={editItem} serviceCode={serviceCode} />}
	/>
);

export default AdpRateMappingForm;
